'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Home', href: '/home-page' },
  { label: 'Lessons', href: '/lesson-library' },
  { label: 'Modules', href: '/modules' },
  { label: 'Subscription', href: '/subscription' },
  { label: 'Dashboard', href: '/teacher-dashboard' },
];

export default function Topbar() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-nav'
            : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/home-page" className="flex items-center gap-2.5 group">
              <AppLogo size={36} />
              <span className="font-display font-semibold text-lg text-stone-800 hidden sm:block leading-tight">
                Teach Global
                <span className="block text-xs font-sans font-500 text-primary-500 tracking-wide uppercase">
                  Citizenship
                </span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks?.map((link) => (
                <Link
                  key={`nav-${link?.href}`}
                  href={link?.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    pathname === link?.href
                      ? 'bg-primary-50 text-primary-600' :'text-stone-600 hover:text-stone-900 hover:bg-stone-100'
                  }`}
                >
                  {link?.label}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-3">
              <button className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-100 transition-all duration-150">
                <Icon name="MagnifyingGlassIcon" size={16} />
                <span className="hidden lg:inline">Search</span>
              </button>
              <Link
                href="/teacher-dashboard"
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-stone-700 border border-stone-200 hover:border-primary-300 hover:text-primary-600 transition-all duration-150"
              >
                Sign In
              </Link>
              <Link
                href="/subscription"
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 active:scale-95 transition-all duration-150 shadow-sm"
              >
                Start Free
              </Link>
              {/* Mobile hamburger */}
              <button
                onClick={() => setDrawerOpen(true)}
                className="md:hidden p-2 rounded-lg text-stone-600 hover:bg-stone-100 transition-all duration-150"
                aria-label="Open navigation menu"
              >
                <Icon name="Bars3Icon" size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl animate-slide-up flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <div className="flex items-center gap-2.5">
                <AppLogo size={32} />
                <span className="font-display font-semibold text-base text-stone-800">
                  Teach Global
                </span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-lg text-stone-500 hover:bg-stone-100 transition-all duration-150"
                aria-label="Close navigation menu"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
              {navLinks?.map((link) => (
                <Link
                  key={`drawer-${link?.href}`}
                  href={link?.href}
                  onClick={() => setDrawerOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 ${
                    pathname === link?.href
                      ? 'bg-primary-50 text-primary-600' :'text-stone-700 hover:bg-stone-50'
                  }`}
                >
                  {link?.label}
                </Link>
              ))}
            </nav>
            <div className="px-4 py-4 border-t border-stone-100 space-y-2">
              <Link
                href="/teacher-dashboard"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center justify-center w-full px-4 py-3 rounded-xl text-sm font-medium text-stone-700 border border-stone-200 hover:border-primary-300 transition-all duration-150"
              >
                Sign In
              </Link>
              <Link
                href="/subscription"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center justify-center w-full px-4 py-3 rounded-xl text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 transition-all duration-150"
              >
                Start Free
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}